import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiUrl } from '@/lib/api';
import type { User, AuthContextType } from '@/types';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    if (token) {
      const fetchUser = async () => {
        try {
          const response = await fetch(getApiUrl('/api/auth/me'), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (response.ok) {
            const userData: User = await response.json();
            setUser(userData);
          } else {
            logout();
          }
        } catch (error) {
          console.error('Failed to fetch user:', error);
          logout();
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email: string, password: string): Promise<User> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', getApiUrl('/api/auth/login'), true);
      xhr.setRequestHeader('Content-Type', 'application/json');

      xhr.onload = function () {
        try {
          const data = JSON.parse(xhr.responseText) as { access_token: string; user: User };
          if (xhr.status >= 200 && xhr.status < 300) {
            localStorage.setItem('token', data.access_token);
            setToken(data.access_token);
            setUser(data.user);
            resolve(data.user);
          } else {
            reject(new Error((data as { detail?: string }).detail || 'Login failed'));
          }
        } catch {
          reject(new Error('Invalid server response'));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  const register = async (
    userData: { email: string; password: string; role?: string; [key: string]: unknown }
  ): Promise<User> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', getApiUrl('/api/auth/register'), true);
      xhr.setRequestHeader('Content-Type', 'application/json');

      xhr.onload = function () {
        try {
          const data = JSON.parse(xhr.responseText) as { access_token: string; user: User };
          if (xhr.status >= 200 && xhr.status < 300) {
            localStorage.setItem('token', data.access_token);
            setToken(data.access_token);
            setUser(data.user);
            resolve(data.user);
          } else {
            reject(new Error((data as { detail?: string }).detail || 'Registration failed'));
          }
        } catch {
          reject(new Error('Invalid server response'));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(JSON.stringify(userData));
    });
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
